import type { SeoProps } from './Seo'

export type ChangeFreq =
  | 'always'
  | 'hourly'
  | 'daily'
  | 'weekly'
  | 'monthly'
  | 'yearly'
  | 'never'

export interface SeoRouteConfig {
  path: string
  title: string
  description: string
  /** Source locale for independently indexable localized utility routes. */
  locale?: string
  /** Fully resolved reciprocal links for an explicitly localized source route. */
  alternates?: readonly AlternateLink[]
  /** Stable source-owned ISO date (YYYY-MM-DD) for sitemap <lastmod>. */
  lastmod?: string
  ogImage?: string
  changefreq?: ChangeFreq
  priority?: number
  noindex?: boolean
  jsonLd?: JsonLdExpectation
}

export interface AlternateLink {
  hreflang: string
  href: string
}

export type PageAlternates =
  | { mode: 'localized'; links: readonly AlternateLink[] }
  | { mode: 'none'; reason: string }

export interface PageRobotsMetadata {
  index: boolean
  follow: boolean
}

export interface PageSocialMetadata {
  title: string
  description: string
  url: string
  image?: string
}

/**
 * Fully resolved metadata consumed by both static HTML generation and runtime
 * navigation. Unlike SeoRouteConfig, this contract contains no implicit URL,
 * robots, locale, social, schema, or sitemap state.
 */
export interface PageSeoMetadata {
  path: string
  title: string
  description: string
  canonical: string
  locale: string
  alternates: PageAlternates
  robots: PageRobotsMetadata
  openGraph: PageSocialMetadata & { type: 'website' | 'article' }
  twitter: PageSocialMetadata & { card: 'summary' | 'summary_large_image' }
  schema: JsonLdExpectation
  sitemap: {
    include: boolean
    lastmod?: string
    changefreq?: ChangeFreq
    priority?: number
  }
}

export interface ResolvePageSeoOptions {
  locale?: string
  canonicalPath?: string
  alternates?: readonly AlternateLink[]
  alternatesNoneReason?: string
  schema?: JsonLdExpectation
  schemaNoneReason?: string
  openGraphType?: 'website' | 'article'
  defaultImage?: string
}

export type JsonLdExpectation =
  | {
      mode: 'required'
      schemas: readonly object[]
    }
  | {
      mode: 'none'
      reason: string
    }

export interface UmbrellaSeoRoutes<K extends string = string> {
  domain: string
  routes: Record<K, SeoRouteConfig>
}

/** Materializes an explicit schema opt-out for legacy app route registries. */
export function withDefaultJsonLd<K extends string>(
  config: UmbrellaSeoRoutes<K>,
  reason: string,
): UmbrellaSeoRoutes<K> {
  if (!reason.trim()) throw new Error('Default JSON-LD opt-out reason must not be empty')
  return {
    ...config,
    routes: Object.fromEntries(Object.entries(config.routes).map(([key, route]) => [
      key,
      {
        ...(route as SeoRouteConfig),
        jsonLd: (route as SeoRouteConfig).jsonLd ?? { mode: 'none', reason },
      },
    ])) as Record<K, SeoRouteConfig>,
  }
}

export function buildCanonical(domain: string, path: string): string {
  const normalized = path === '/' ? '' : path
  return `https://${domain}${normalized}`
}

export function resolvePageSeoMetadata(
  domain: string,
  route: SeoRouteConfig,
  options: ResolvePageSeoOptions = {},
): PageSeoMetadata {
  const canonical = buildCanonical(domain, options.canonicalPath ?? route.path)
  const locale = options.locale ?? route.locale ?? 'ru'
  const alternates = options.alternates ?? route.alternates
  const image = route.ogImage ?? options.defaultImage
  const social = {
    title: route.title,
    description: route.description,
    url: canonical,
    ...(image ? { image } : {}),
  }

  return {
    path: route.path,
    title: route.title,
    description: route.description,
    canonical,
    locale,
    alternates: alternates && alternates.length > 0
      ? { mode: 'localized', links: alternates }
      : {
          mode: 'none',
          reason: options.alternatesNoneReason ??
            'This source route has no separately indexable localized URL.',
        },
    robots: {
      index: !route.noindex,
      follow: true,
    },
    openGraph: {
      ...social,
      type: options.openGraphType ?? 'website',
    },
    twitter: {
      ...social,
      card: image ? 'summary_large_image' : 'summary',
    },
    schema: options.schema ?? route.jsonLd ?? {
      mode: 'none',
      reason: options.schemaNoneReason ?? 'No structured data applies to this source route.',
    },
    sitemap: {
      include: !route.noindex,
      lastmod: route.lastmod,
      changefreq: route.changefreq,
      priority: route.priority,
    },
  }
}

export function toSeoProps(
  domain: string,
  route: SeoRouteConfig,
  options?: ResolvePageSeoOptions,
): SeoProps {
  const metadata = resolvePageSeoMetadata(domain, route, options)
  return {
    metadata,
    title: metadata.title,
    description: metadata.description,
    canonical: metadata.canonical,
    noindex: !metadata.robots.index,
    ogImage: metadata.openGraph.image,
  }
}
